import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Upload, Wand2, CheckCircle2, AlertTriangle, ArrowDownLeft, ArrowUpRight,
  Filter, Search, Sparkles, Brain, RotateCcw, ChevronLeft, ChevronDown, ChevronRight,
  Lock, Edit3, Zap, FilePlus2, Split, Trash2, Layers, X, FileText,
} from "lucide-react";
import api, { formatINR, describeAxiosError } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import RequireCompany from "@/components/RequireCompany";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/* ────────────────────────────────────────────────────────────────────────
   Bank Statement → AI Ledger Recommendation → Bulk Post
   Flow:  Upload → Backend extracts rows + suggests ledger per row →
          User reviews / overrides / splits ledgers → Bulk-post creates
          vouchers. Session never auto-posts without explicit user approval.
   ──────────────────────────────────────────────────────────────────────── */

const HIGH_CONFIDENCE_THRESHOLD = 85;   // green "High Confidence" cutoff

const SOURCE_BADGE = {
  learning: { label: "Learned",  cls: "bg-purple-100 text-purple-800 border-purple-200" },
  rule:     { label: "Rule",     cls: "bg-blue-100   text-blue-800   border-blue-200"   },
  ai:       { label: "AI",       cls: "bg-amber-100  text-amber-800  border-amber-200"  },
  "":       { label: "Unmapped", cls: "bg-gray-100   text-gray-600   border-gray-200"   },
};

const LEDGER_GROUPS = [
  "Indirect Expenses", "Direct Expenses", "Indirect Income", "Direct Income",
  "Sundry Debtors", "Sundry Creditors", "Duties & Taxes", "Bank Accounts",
  "Cash-in-Hand", "Current Assets", "Current Liabilities", "Fixed Assets",
  "Loans (Liability)", "Capital Account", "Purchase Accounts", "Sales Accounts",
  "Investments", "Provisions",
];

const NEW_LEDGER = "__create_new_ledger__";

const ConfidenceBar = ({ value }) => {
  const v = Math.max(0, Math.min(100, Number(value) || 0));
  const color = v >= HIGH_CONFIDENCE_THRESHOLD ? "bg-emerald-500" : v >= 65 ? "bg-amber-500" : "bg-gray-400";
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${v}%` }} />
      </div>
      <span className="text-[10px] font-mono text-gray-600 w-7 text-right">{v}%</span>
    </div>
  );
};

/** Small helper to normalise a narration into a search-safe keyword. */
const firstMeaningfulKeyword = (str) => {
  const stop = new Set(["upi", "neft", "imps", "rtgs", "atm", "pos", "the", "and", "ref", "no", "id", "to", "from", "at"]);
  const parts = (str || "").toLowerCase().replace(/[^a-z0-9\s]/g, " ").split(/\s+/).filter(Boolean);
  return parts.find((p) => p.length >= 4 && !stop.has(p)) || parts[0] || "";
};

function Inner() {
  const { activeId } = useCompany();
  const nav = useNavigate();
  const [ledgers, setLedgers] = useState([]);
  const [bankId, setBankId] = useState("");
  const [useAI, setUseAI] = useState(true);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [posting, setPosting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [parsingReport, setParsingReport] = useState(null);
  const [overrides, setOverrides] = useState({});     // {idx: { contra_ledger_id, approved, narration_override }}
  const [splitsByRow, setSplitsByRow] = useState({}); // {idx: [{contra_ledger_id, amount, narration}]}
  const [expandedSplits, setExpandedSplits] = useState({});
  const [expandedGroups, setExpandedGroups] = useState({});
  const [skipDupes, setSkipDupes] = useState(true);
  const [useOcr, setUseOcr] = useState(true);
  const [search, setSearch] = useState("");
  const [filterSource, setFilterSource] = useState("all");
  const [lastError, setLastError] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [bankSearch, setBankSearch] = useState("");
  // Master toggles
  const [autoApproveHighConf, setAutoApproveHighConf] = useState(true);
  const [groupIdentical, setGroupIdentical] = useState(false);
  // Rule creation modal
  const [ruleModal, setRuleModal] = useState(null);   // { idx, keyword, ledger_id }
  // Keyboard navigation
  const [focusedRowIdx, setFocusedRowIdx] = useState(null);
  const rowRefs = useRef({});
  const ledgerSelectRefs = useRef({});
  const fileInputRef = useRef(null);
  // Async job progress (Cloudflare timeout fix — async import pipeline)
  const [jobProgress, setJobProgress] = useState(null); // { stage, message, pct }
  const jobPollRef = useRef(null);

  const load = useCallback(async () => {
    if (!activeId) return;
    const { data } = await api.get(`/companies/${activeId}/ledgers`);
    setLedgers(data || []);
    const banks = (data || []).filter((L) => L.group === "Bank Accounts" || L.group === "Cash-in-Hand");
    if (banks.length && !bankId) setBankId(banks[0].id);
  }, [activeId, bankId]);

  useEffect(() => { load(); }, [load]);

  const bankLedgers = useMemo(
    () => ledgers.filter((L) => L.group === "Bank Accounts" || L.group === "Cash-in-Hand"),
    [ledgers],
  );
  const filteredBanks = useMemo(() => {
    const q = bankSearch.trim().toLowerCase();
    if (!q) return bankLedgers;
    return bankLedgers.filter((L) => L.name.toLowerCase().includes(q));
  }, [bankLedgers, bankSearch]);

  /* ─── Drag / drop ─────────────────────────────────────────────────── */
  const onDrag = (e, active) => {
    e.preventDefault(); e.stopPropagation();
    setDragActive(active);
  };
  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    setDragActive(false);
    const f = e.dataTransfer.files?.[0];
    if (f) setFile(f);
  };

  /* ─── Async job polling helpers ─────────────────────────────────── */
  const stopPolling = () => {
    if (jobPollRef.current) {
      clearInterval(jobPollRef.current);
      jobPollRef.current = null;
    }
  };
  // Cleanup poll on unmount
  useEffect(() => stopPolling, []);

  /** Poll a background import job until it reaches `ready` or `failed`.
   *  Updates `jobProgress` on every tick so the progress bar UI stays live. */
  const pollJob = (jobId, dryRun) => new Promise((resolve, reject) => {
    stopPolling();
    let ticks = 0;
    const MAX_TICKS = 300; // 300 × 1.5s = 7.5 min upper bound
    jobPollRef.current = setInterval(async () => {
      ticks += 1;
      if (ticks > MAX_TICKS) {
        stopPolling();
        return reject(new Error("Timed out waiting for the import to complete."));
      }
      try {
        const { data } = await api.get(`/companies/${activeId}/bank/import-jobs/${jobId}`);
        setJobProgress({
          stage: data.stage,
          message: data.message,
          pct: data.progress_pct,
          filename: data.filename,
        });
        if (data.status === "ready") { stopPolling(); resolve({ ...data.result, dry_run: dryRun }); }
        else if (data.status === "failed") { stopPolling(); reject(data.error || { message: "Import failed" }); }
      } catch (err) {
        // Transient network error — keep polling. Only give up on 404.
        if (err?.response?.status === 404) {
          stopPolling();
          reject(new Error("Job not found — server may have restarted."));
        }
      }
    }, 1500);
  });

  /* ─── Upload & recommend ─────────────────────────────────────────── */
  const doUpload = async (dryRun = false) => {
    if (!bankId) { toast.error("Pick a bank ledger first"); return; }
    if (!file)   { toast.error("Choose a CSV / XLSX / XLS / PDF file"); return; }
    setBusy(true);
    setLastError(null);
    setJobProgress({ stage: "queued", message: "Uploading …", pct: 0, filename: file.name });
    const fd = new FormData();
    fd.append("file", file);
    try {
      // Start async job — returns in <500 ms so we dodge Cloudflare's
      // 100 s edge timeout even on multi-minute OCR+AI pipelines.
      const { data: startResp } = await api.post(
        `/companies/${activeId}/bank/${bankId}/import-recommend/start?use_ai=${useAI}&use_ocr=${useOcr}&dry_run=${dryRun}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 },
      );
      const data = await pollJob(startResp.job_id, dryRun);
      setParsingReport(data.parsing_report || null);
      if (dryRun) {
        const previewShape = {
          rows: data.rows,
          summary: {
            total: data.rows.length,
            auto_recommended: 0,
            duplicates: 0,
            total_inflow: data.rows.reduce((s, r) => s + (r.deposit || 0), 0),
            total_outflow: data.rows.reduce((s, r) => s + (r.withdrawal || 0), 0),
            by_source: { learning: 0, rule: 0, ai: 0, none: data.rows.length },
          },
          ledger_name: data.ledger_name,
          import_log_id: null,
          dry_run: true,
        };
        setPreview(previewShape);
        toast.success(`Preview: ${data.rows.length} rows parsed (no ledger recommendation yet)`);
        return;
      }
      setPreview(data);
      const seen = new Set(ledgers.map((L) => L.id));
      const suggestedNew = [];
      (data.rows || []).forEach((r) => {
        if (r.suggestion && !seen.has(r.suggestion.id)) {
          seen.add(r.suggestion.id);
          suggestedNew.push(r.suggestion);
        }
      });
      if (suggestedNew.length) {
        setLedgers((curr) => [...curr, ...suggestedNew].sort((a, b) => a.name.localeCompare(b.name)));
      }
      // Auto-approve rows with confidence ≥ HIGH_CONFIDENCE_THRESHOLD when
      // the master toggle is on. Everyone else stays un-approved.
      const init = {};
      (data.rows || []).forEach((r, i) => {
        const highConf = (r.confidence || 0) >= HIGH_CONFIDENCE_THRESHOLD;
        init[i] = {
          contra_ledger_id: r.suggestion?.id || "",
          approved: Boolean(r.suggestion && !r.duplicate_likely && (autoApproveHighConf ? highConf : true)),
        };
      });
      setOverrides(init);
      setSplitsByRow({});
      setExpandedSplits({});
      setExpandedGroups({});
      toast.success(
        `Parsed ${data.summary.total} rows · ${data.summary.auto_recommended} recommended` +
        (data.summary.duplicates ? ` · ⚠ ${data.summary.duplicates} potential duplicates` : ""),
      );
    } catch (e) {
      // `e` may be an Axios error, a background-job error payload
      // ({ message, parsing_report }), or a plain Error.
      const detail = e?.response?.data?.detail || e;
      const msg = detail?.message || describeAxiosError(e, "Import failed");
      const report = detail?.parsing_report || null;
      setParsingReport(report);
      setLastError({ message: msg, report });
      toast.error(msg);
    } finally {
      setBusy(false);
      setJobProgress(null);
      stopPolling();
    }
  };

  /* ─── Row edits ──────────────────────────────────────────────────── */
  const toggleRow = (idx) => setOverrides((o) => ({ ...o, [idx]: { ...o[idx], approved: !o[idx]?.approved } }));
  const setRowLedger = (idx, lid) =>
    setOverrides((o) => ({ ...o, [idx]: { ...o[idx], contra_ledger_id: lid, approved: Boolean(lid) } }));
  const setRowNarration = (idx, val) =>
    setOverrides((o) => ({ ...o, [idx]: { ...o[idx], narration_override: val } }));

  /* ─── Create ledger straight from the destination dropdown ───────── */
  const [createLedger, setCreateLedger] = useState(null); // { idx, splitK, name, group }
  const groupOptions = useMemo(() => {
    const s = new Set((ledgers || []).map((L) => L.group).filter(Boolean));
    LEDGER_GROUPS.forEach((g) => s.add(g));
    return Array.from(s).sort();
  }, [ledgers]);
  // The bank being imported can never be its own destination/contra ledger —
  // that produces a self-contra voucher (Bank Dr + Bank Cr) which shows the
  // same entry twice and corrupts the closing balance. Keep it out of every
  // destination picker.
  const contraLedgers = useMemo(
    () => (ledgers || []).filter((L) => L.id !== bankId),
    [ledgers, bankId],
  );
  const requestCreateLedger = (idx, splitK = null) => {
    const r = preview?.rows?.[idx];
    const isCredit = r && r.deposit > 0;
    setCreateLedger({ idx, splitK, name: "", group: isCredit ? "Indirect Income" : "Indirect Expenses" });
  };
  const saveNewLedger = async () => {
    const name = (createLedger?.name || "").trim();
    if (!name) { toast.error("Enter a ledger name"); return; }
    if (!createLedger?.group) { toast.error("Pick a group"); return; }
    try {
      const { data } = await api.post(`/companies/${activeId}/ledgers`, { name, group: createLedger.group });
      setLedgers((curr) => [...curr, data].sort((a, b) => a.name.localeCompare(b.name)));
      if (createLedger.splitK != null) updateSplitLine(createLedger.idx, createLedger.splitK, { contra_ledger_id: data.id });
      else setRowLedger(createLedger.idx, data.id);
      toast.success(`Created "${data.name}" and assigned to this row`);
      setCreateLedger(null);
    } catch (e) { toast.error(describeAxiosError(e, "Could not create ledger")); }
  };

  const [reconciling, setReconciling] = useState(false);
  const reconcileOpening = async () => {
    const rec = preview?.reconciliation;
    if (!rec || rec.statement_opening == null || !rec.first_txn_date) return;
    setReconciling(true);
    try {
      const { data } = await api.post(
        `/companies/${activeId}/bank/${bankId}/reconcile-opening`,
        { statement_opening: rec.statement_opening, as_of_date: rec.first_txn_date },
      );
      // Recompute reconciliation locally so the UI reflects the tally instantly.
      setPreview((p) => {
        if (!p?.reconciliation) return p;
        const r = { ...p.reconciliation };
        r.ledger_opening_before_period = r.statement_opening;
        r.ledger_opening_matches = true;
        r.opening_gap = 0;
        r.projected_ledger_closing = r.statement_closing;
        r.projected_matches_statement = true;
        return { ...p, reconciliation: r };
      });
      toast.success(`Opening balance set to ${formatINR(data.opening_balance)} ${data.balance_type} — statement will now tally`);
    } catch (e) {
      toast.error(describeAxiosError(e, "Could not set opening balance"));
    } finally {
      setReconciling(false);
    }
  };

  const [repairing, setRepairing] = useState(false);
  const [repairFound, setRepairFound] = useState(null);
  useEffect(() => { setRepairFound(null); }, [bankId]);
  const checkSelfContra = async () => {
    if (!bankId) { toast.error("Choose the bank ledger first"); return; }
    setRepairing(true);
    try {
      const { data } = await api.post(`/companies/${activeId}/bank/${bankId}/repair-self-contra?dry_run=true`);
      setRepairFound(data.found);
      if (!data.found) toast.success("No duplicate (debit + credit) entries found for this bank 🎉");
    } catch (e) { toast.error(describeAxiosError(e, "Check failed")); }
    finally { setRepairing(false); }
  };
  const deleteSelfContra = async () => {
    setRepairing(true);
    try {
      const { data } = await api.post(`/companies/${activeId}/bank/${bankId}/repair-self-contra?dry_run=false`);
      toast.success(`Removed ${data.deleted} corrupt voucher(s). Re-import the statement to post them correctly.`, { duration: 7000 });
      setRepairFound(0);
    } catch (e) { toast.error(describeAxiosError(e, "Repair failed")); }
    finally { setRepairing(false); }
  };

  const reclassifyRow = async (idx) => {
    const r = preview.rows[idx];
    const narration = (overrides[idx]?.narration_override || r.description || "").trim();
    if (!narration) return;
    try {
      const { data } = await api.post(`/companies/${activeId}/bank/reclassify`, {
        narrations: [narration],
        is_credit_per_row: [r.deposit > 0],
      });
      const result = data.results?.[0];
      if (!result?.suggestion) {
        toast.message("Still no confident match — try editing the narration.");
        return;
      }
      setLedgers((curr) => curr.some((L) => L.id === result.suggestion.id) ? curr : [...curr, result.suggestion]);
      setPreview((p) => {
        const next = { ...p, rows: [...p.rows] };
        next.rows[idx] = {
          ...next.rows[idx],
          suggestion: result.suggestion,
          confidence: result.confidence,
          reason: result.reason,
          source: result.source,
        };
        return next;
      });
      setOverrides((o) => ({ ...o, [idx]: { ...o[idx], contra_ledger_id: result.suggestion.id, approved: true } }));
      toast.success(`Re-classified → ${result.suggestion.name} (${result.confidence}%)`);
    } catch (e) {
      toast.error(describeAxiosError(e, "Reclassify failed"));
    }
  };

  /* ─── Search + filter + grouping ─────────────────────────────────── */
  const filtered = useMemo(() => {
    if (!preview) return [];
    const q = search.trim().toLowerCase();
    return preview.rows.map((r, i) => ({ ...r, _idx: i })).filter((r) => {
      if (filterSource !== "all" && (r.source || "") !== (filterSource === "none" ? "" : filterSource)) return false;
      if (!q) return true;
      return (
        r.description.toLowerCase().includes(q) ||
        (r.ref || "").toLowerCase().includes(q) ||
        (r.suggestion?.name || "").toLowerCase().includes(q)
      );
    });
  }, [preview, search, filterSource]);

  // Grouping mode: bucket rows by normalized narration keyword.
  const grouped = useMemo(() => {
    if (!groupIdentical) return null;
    const buckets = new Map();
    filtered.forEach((r) => {
      const key = firstMeaningfulKeyword(r.description);
      if (!buckets.has(key)) buckets.set(key, { key, sample: r, items: [] });
      buckets.get(key).items.push(r);
    });
    // Sort by size desc so the biggest repeats show first.
    return Array.from(buckets.values()).sort((a, b) => b.items.length - a.items.length);
  }, [filtered, groupIdentical]);

  const approvedCount = useMemo(() => {
    return preview ? preview.rows.reduce((acc, _r, i) => {
      const ov = overrides[i];
      const hasSplits = (splitsByRow[i] || []).length > 0;
      if (ov?.approved && (ov?.contra_ledger_id || hasSplits)) return acc + 1;
      return acc;
    }, 0) : 0;
  }, [preview, overrides, splitsByRow]);

  const invalidSplits = useMemo(() => {
    if (!preview) return [];
    const bad = [];
    Object.entries(splitsByRow).forEach(([idx, splits]) => {
      if (!splits || splits.length === 0) return;
      const r = preview.rows[Number(idx)];
      if (!r) return;
      const total = r.deposit > 0 ? r.deposit : r.withdrawal;
      const sum = splits.reduce((s, x) => s + Number(x.amount || 0), 0);
      if (Math.abs(sum - total) > 0.01) bad.push({ idx: Number(idx), total, sum });
    });
    return bad;
  }, [preview, splitsByRow]);

  /* ─── Bulk actions ──────────────────────────────────────────────── */
  const selectAll = (checked) => {
    if (!preview) return;
    setOverrides((o) => {
      const next = { ...o };
      filtered.forEach((r) => {
        if (next[r._idx]?.contra_ledger_id || (splitsByRow[r._idx] || []).length > 0) {
          next[r._idx] = { ...next[r._idx], approved: checked };
        }
      });
      return next;
    });
  };

  const bulkAssign = (lid) => {
    if (!preview || !lid) return;
    setOverrides((o) => {
      const next = { ...o };
      filtered.forEach((r) => { next[r._idx] = { contra_ledger_id: lid, approved: true }; });
      return next;
    });
    toast.success(`Assigned ${filtered.length} visible rows`);
  };

  const applyLedgerToGroup = (indices, lid) => {
    if (!lid) return;
    setOverrides((o) => {
      const next = { ...o };
      indices.forEach((i) => { next[i] = { contra_ledger_id: lid, approved: true }; });
      return next;
    });
    toast.success(`Mapped ${indices.length} rows to selected ledger`);
  };

  /* ─── Split-row helpers ─────────────────────────────────────────── */
  const originalAmount = (row) => (row.deposit > 0 ? row.deposit : row.withdrawal);

  const openSplit = (idx) => {
    setExpandedSplits((s) => ({ ...s, [idx]: true }));
    setSplitsByRow((sp) => {
      if (sp[idx] && sp[idx].length > 0) return sp;
      const r = preview.rows[idx];
      const total = originalAmount(r);
      // Seed with two lines — first pre-filled with any suggested ledger.
      return {
        ...sp,
        [idx]: [
          { contra_ledger_id: r.suggestion?.id || "", amount: Number((total / 2).toFixed(2)), narration: "" },
          { contra_ledger_id: "", amount: Number((total - total / 2).toFixed(2)), narration: "" },
        ],
      };
    });
    // Ensure the row is treated as approved once the user starts splitting.
    setOverrides((o) => ({ ...o, [idx]: { ...(o[idx] || {}), contra_ledger_id: "", approved: true } }));
  };

  const closeSplit = (idx) => {
    setExpandedSplits((s) => ({ ...s, [idx]: false }));
    setSplitsByRow((sp) => {
      const next = { ...sp };
      delete next[idx];
      return next;
    });
  };

  const updateSplitLine = (idx, k, patch) => {
    setSplitsByRow((sp) => {
      const arr = [...(sp[idx] || [])];
      arr[k] = { ...arr[k], ...patch };
      return { ...sp, [idx]: arr };
    });
  };

  const addSplitLine = (idx) => {
    setSplitsByRow((sp) => ({ ...sp, [idx]: [...(sp[idx] || []), { contra_ledger_id: "", amount: 0, narration: "" }] }));
  };

  const removeSplitLine = (idx, k) => {
    setSplitsByRow((sp) => {
      const arr = [...(sp[idx] || [])];
      arr.splice(k, 1);
      return { ...sp, [idx]: arr };
    });
  };

  const balanceSplit = (idx) => {
    // Auto-fill the LAST empty line to make the total balance.
    const r = preview.rows[idx];
    const total = originalAmount(r);
    setSplitsByRow((sp) => {
      const arr = [...(sp[idx] || [])];
      if (arr.length === 0) return sp;
      const already = arr.slice(0, -1).reduce((s, x) => s + Number(x.amount || 0), 0);
      arr[arr.length - 1] = { ...arr[arr.length - 1], amount: Number((total - already).toFixed(2)) };
      return { ...sp, [idx]: arr };
    });
  };

  /* ─── Rule modal ─────────────────────────────────────────────────── */
  const openRuleModal = (idx) => {
    const r = preview.rows[idx];
    const ov = overrides[idx] || {};
    setRuleModal({
      idx,
      keyword: firstMeaningfulKeyword(r.description),
      ledger_id: ov.contra_ledger_id || r.suggestion?.id || "",
    });
  };
  const saveRule = async () => {
    if (!ruleModal || !ruleModal.keyword.trim() || !ruleModal.ledger_id) return;
    try {
      await api.post(`/companies/${activeId}/bank/mappings`, {
        keyword: ruleModal.keyword.trim(),
        ledger_id: ruleModal.ledger_id,
        apply_to_current: true,
      });
      toast.success(`Rule saved: "${ruleModal.keyword}" → ledger`);
      // Cascade the mapping to current rows containing the keyword.
      const kw = ruleModal.keyword.toLowerCase();
      const led = ledgers.find((L) => L.id === ruleModal.ledger_id);
      setOverrides((o) => {
        const next = { ...o };
        preview.rows.forEach((r, i) => {
          if ((r.description || "").toLowerCase().includes(kw)) {
            next[i] = { contra_ledger_id: ruleModal.ledger_id, approved: true };
          }
        });
        return next;
      });
      setPreview((p) => {
        if (!p) return p;
        const rows = p.rows.map((r) => {
          if ((r.description || "").toLowerCase().includes(kw)) {
            return {
              ...r, source: "rule",
              suggestion: led ? { id: led.id, name: led.name, group: led.group } : r.suggestion,
              confidence: 100, reason: `Matched user-rule "${ruleModal.keyword}"`,
            };
          }
          return r;
        });
        return { ...p, rows };
      });
      setRuleModal(null);
    } catch (e) {
      toast.error(describeAxiosError(e, "Failed to save rule"));
    }
  };

  /* ─── Post ───────────────────────────────────────────────────────── */
  const postSelected = async () => {
    if (!preview) return;
    if (invalidSplits.length > 0) {
      toast.error(`Fix ${invalidSplits.length} split(s) with mismatched totals before posting`);
      return;
    }
    const rows = [];
    preview.rows.forEach((r, i) => {
      const ov = overrides[i];
      const splits = splitsByRow[i];
      if (!ov?.approved) return;

      if (splits && splits.length > 0) {
        rows.push({
          date: r.date,
          description: r.description,
          ref: r.ref || "",
          withdrawal: r.withdrawal,
          deposit: r.deposit,
          balance: r.balance,
          narration_override: ov?.narration_override || "",
          splits: splits.map((s) => ({
            contra_ledger_id: s.contra_ledger_id,
            amount: Number(s.amount) || 0,
            narration: s.narration || "",
          })),
        });
      } else if (ov?.contra_ledger_id) {
        rows.push({
          date: r.date,
          description: r.description,
          ref: r.ref || "",
          withdrawal: r.withdrawal,
          deposit: r.deposit,
          balance: r.balance,
          contra_ledger_id: ov.contra_ledger_id,
          narration_override: ov?.narration_override || "",
        });
      }
    });
    if (rows.length === 0) { toast.error("Approve at least one row first"); return; }
    setPosting(true);
    try {
      const { data } = await api.post(`/companies/${activeId}/bank/post-statement-vouchers`, {
        bank_ledger_id: bankId,
        rows,
        import_log_id: preview.import_log_id,
        skip_duplicates: skipDupes,
      });
      const dupMsg = data.skipped_duplicates ? ` · ${data.skipped_duplicates} duplicate(s) skipped` : "";
      toast.success(`${data.posted} voucher(s) posted${dupMsg}`);
      if (data.skipped_self_contra) {
        toast.warning(
          `${data.skipped_self_contra} row(s) were skipped because their destination ledger was the bank itself — pick a proper expense/income ledger and re-import those.`,
          { duration: 8000 },
        );
      }
      setPreview(null); setFile(null); setOverrides({}); setSplitsByRow({});
    } catch (e) {
      toast.error(describeAxiosError(e, "Posting failed"));
    } finally { setPosting(false); }
  };

  const reset = () => {
    setPreview(null); setFile(null); setOverrides({});
    setSplitsByRow({}); setExpandedSplits({}); setExpandedGroups({});
    setSearch(""); setFilterSource("all"); setParsingReport(null); setLastError(null);
  };

  /* ─── Keyboard navigation (Up/Down/Enter/Tab) ─────────────────── */
  const rowsForNav = useMemo(() => filtered.map((r) => r._idx), [filtered]);

  useEffect(() => {
    const handler = (e) => {
      if (!preview || ruleModal) return;
      const active = document.activeElement;
      const tag = (active?.tagName || "").toLowerCase();
      // Skip navigation when user is typing in text-ish fields
      if (["textarea"].includes(tag) || (tag === "input" && active?.type === "text")) return;

      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        setFocusedRowIdx((cur) => {
          if (rowsForNav.length === 0) return null;
          if (cur === null) return rowsForNav[0];
          const i = rowsForNav.indexOf(cur);
          if (i === -1) return rowsForNav[0];
          const next = e.key === "ArrowDown" ? Math.min(rowsForNav.length - 1, i + 1) : Math.max(0, i - 1);
          const idx = rowsForNav[next];
          rowRefs.current[idx]?.scrollIntoView({ block: "nearest", inline: "nearest" });
          return idx;
        });
      } else if (e.key === "Enter" && focusedRowIdx !== null) {
        e.preventDefault();
        ledgerSelectRefs.current[focusedRowIdx]?.focus();
      } else if (e.key === " " && focusedRowIdx !== null) {
        // Space toggles approval
        e.preventDefault();
        toggleRow(focusedRowIdx);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [preview, ruleModal, rowsForNav, focusedRowIdx]);

  /* ────────────────────────────────────────────────────────────────── */

  return (
    <>
      <PageHeader
        title="Import Bank Statement"
        subtitle="Drag-drop a statement · AI recommends contra ledgers · Review · Bulk-post as vouchers"
        actions={
          <Button variant="ghost" onClick={() => nav("/banking")} className="h-8 text-sm" data-testid="btn-back-banking">
            <ChevronLeft className="w-4 h-4 mr-1" /> Back to Banking
          </Button>
        }
      />

      <div className="p-6 space-y-4">
        {!preview && (
          <div className="border border-gray-200 rounded bg-white p-5 space-y-4" data-testid="upload-card">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Searchable bank ledger dropdown */}
              <div className="space-y-1.5">
                <label className="text-xs uppercase tracking-wider text-gray-600">Target Bank Account Ledger *</label>
                <Input
                  value={bankSearch}
                  onChange={(e) => setBankSearch(e.target.value)}
                  placeholder="Search bank ledger…"
                  className="h-9 text-sm mb-1"
                  data-testid="input-bank-search"
                />
                <select
                  value={bankId}
                  onChange={(e) => setBankId(e.target.value)}
                  className="h-9 w-full rounded border border-gray-300 px-2 text-sm bg-white"
                  data-testid="select-bank-ledger"
                >
                  <option value="">-- choose --</option>
                  {filteredBanks.map((L) => <option key={L.id} value={L.id}>{L.name} ({L.group})</option>)}
                </select>
                {bankLedgers.length === 0 && (
                  <p className="text-[11px] text-rose-700 mt-1">
                    No bank/cash ledger exists yet. Create one under Ledgers → Bank Accounts first.
                  </p>
                )}
                {bankId && (
                  <div className="mt-2 text-[11px]">
                    <button
                      type="button"
                      onClick={checkSelfContra}
                      disabled={repairing}
                      className="text-blue-700 hover:underline disabled:opacity-50"
                      data-testid="btn-check-self-contra"
                    >
                      {repairing && repairFound == null ? "Checking…" : "Check for duplicate (debit + credit) entries"}
                    </button>
                    {repairFound > 0 && (
                      <div className="mt-1.5 rounded border border-rose-200 bg-rose-50 p-2 text-rose-800" data-testid="self-contra-banner">
                        Found <strong>{repairFound}</strong> corrupt voucher(s) where this bank is both debited and
                        credited (the same entry shown twice). Deleting them is safe — they net to zero.
                        <Button
                          onClick={deleteSelfContra}
                          disabled={repairing}
                          className="mt-1.5 h-7 text-[11px] bg-rose-600 hover:bg-rose-700"
                          data-testid="btn-delete-self-contra"
                        >
                          {repairing ? "Removing…" : `Delete ${repairFound} corrupt voucher(s)`}
                        </Button>
                        <span className="block mt-1 text-[10px] text-rose-700">Then re-import the statement to post them correctly.</span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Drag-drop zone */}
              <div className="space-y-1.5 lg:col-span-2">
                <label className="text-xs uppercase tracking-wider text-gray-600">Statement file</label>
                <div
                  onDragEnter={(e) => onDrag(e, true)}
                  onDragOver={(e) => onDrag(e, true)}
                  onDragLeave={(e) => onDrag(e, false)}
                  onDrop={onDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className={`rounded border-2 border-dashed p-6 text-center transition cursor-pointer
                    ${dragActive ? "border-blue-600 bg-blue-50" : "border-gray-300 bg-gray-50 hover:border-blue-400 hover:bg-blue-50/30"}`}
                  data-testid="drag-drop-zone"
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === "Enter") fileInputRef.current?.click(); }}
                >
                  <FileText className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                  <div className="text-sm font-medium text-gray-800">
                    {file ? file.name : "Drag & drop your statement here"}
                  </div>
                  <div className="text-[11px] text-gray-500 mt-1">
                    or click to browse · CSV, XLSX, XLS, PDF (up to 25 MB)
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,.xlsx,.xls,.pdf"
                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                    className="hidden"
                    data-testid="input-statement-file"
                  />
                </div>
                {file && (
                  <div className="text-[11px] text-gray-500 flex items-center justify-between">
                    <span>Selected: <strong>{file.name}</strong> ({Math.round(file.size / 1024)} KB)</span>
                    <button onClick={(e) => { e.stopPropagation(); setFile(null); }} className="text-rose-700 hover:underline" data-testid="btn-clear-file">Remove</button>
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center justify-between gap-4 pt-2 flex-wrap">
              <div className="flex items-center gap-5 flex-wrap">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={useAI} onChange={(e) => setUseAI(e.target.checked)} data-testid="checkbox-use-ai" />
                  <Brain className="w-4 h-4 text-amber-600" /> AI fallback (Claude Haiku 4.5)
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={useOcr} onChange={(e) => setUseOcr(e.target.checked)} data-testid="checkbox-use-ocr" />
                  <Sparkles className="w-4 h-4 text-blue-600" /> OCR for scanned PDFs (slow, ~5 s / page)
                </label>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => doUpload(true)}
                  disabled={busy || !file || !bankId}
                  variant="outline" className="h-9"
                  data-testid="btn-preview-statement"
                >
                  {busy ? <><Sparkles className="w-4 h-4 mr-1.5 animate-pulse" />Previewing…</> :
                          <><Search className="w-4 h-4 mr-1.5" />Preview</>}
                </Button>
                <Button
                  onClick={() => doUpload(false)}
                  disabled={busy || !file || !bankId}
                  className="bg-blue-700 hover:bg-blue-800 h-9"
                  data-testid="btn-parse-statement"
                >
                  {busy ? <><Sparkles className="w-4 h-4 mr-1.5 animate-pulse" />Parsing…</> :
                          <><Upload className="w-4 h-4 mr-1.5" />Parse &amp; Recommend</>}
                </Button>
              </div>
            </div>

            {/* Async import progress — replaces the old blank "Parsing…" spinner
                with a live stage indicator. Backend does the heavy lifting off
                the request thread, so Cloudflare's 100 s edge timeout can no
                longer wipe out a multi-minute OCR + AI pipeline. */}
            {jobProgress && (
              <div
                className="mt-3 border border-blue-200 bg-blue-50 rounded p-3"
                data-testid="job-progress-card"
              >
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2 text-sm text-blue-900 font-medium">
                    <Sparkles className="w-4 h-4 animate-pulse" />
                    <span data-testid="job-stage-message">{jobProgress.message || "Working…"}</span>
                  </div>
                  <div className="text-xs font-mono text-blue-700" data-testid="job-progress-pct">
                    {jobProgress.pct || 0}%
                  </div>
                </div>
                <div className="h-1.5 bg-white border border-blue-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-blue-600 transition-all duration-500 ease-out"
                    style={{ width: `${jobProgress.pct || 0}%` }}
                  />
                </div>
                <div className="mt-2 flex items-center gap-1.5 text-[10px] uppercase tracking-wider">
                  {[
                    { key: "reading_pdf",  label: "Read PDF" },
                    { key: "ocr",          label: "OCR" },
                    { key: "extracting",   label: "Extract" },
                    { key: "recommending", label: "AI" },
                    { key: "ready",        label: "Done" },
                  ].map((s) => {
                    const stageOrder = ["queued", "reading_pdf", "ocr", "extracting", "recommending", "ready"];
                    const currIdx = stageOrder.indexOf(jobProgress.stage);
                    const stageIdx = stageOrder.indexOf(s.key);
                    const active = stageIdx <= currIdx;
                    return (
                      <span
                        key={s.key}
                        className={
                          "px-1.5 py-0.5 rounded border " +
                          (active
                            ? "bg-blue-600 text-white border-blue-600"
                            : "bg-white text-gray-400 border-gray-200")
                        }
                        data-testid={`stage-chip-${s.key}`}
                      >
                        {s.label}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            {(parsingReport || lastError) && (
              <div className={`mt-3 rounded border p-3 text-xs space-y-2 ${lastError ? "border-rose-200 bg-rose-50" : "border-blue-200 bg-blue-50"}`} data-testid="parsing-report-card">
                {lastError && (
                  <div className="text-rose-800 font-semibold flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>{lastError.message}</span>
                  </div>
                )}
                {parsingReport && (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <div><span className="text-gray-500">Rows found:</span> <span className="font-mono font-semibold">{parsingReport.totalRowsFound}</span></div>
                    <div><span className="text-gray-500">Parsed:</span> <span className="font-mono font-semibold text-emerald-700">{parsingReport.transactionsParsed}</span></div>
                    <div><span className="text-gray-500">Failed:</span> <span className={`font-mono font-semibold ${parsingReport.transactionsFailed ? "text-rose-700" : ""}`}>{parsingReport.transactionsFailed}</span></div>
                    <div><span className="text-gray-500">Mode:</span> <span className="font-mono font-semibold uppercase">{parsingReport.mode}</span></div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ═════════ Review Grid ═════════ */}
        {preview && (
          <>
            {preview.dry_run && (
              <div className="rounded border border-blue-200 bg-blue-50 p-3 flex items-center justify-between text-sm" data-testid="dry-run-banner">
                <div className="flex items-center gap-2">
                  <Search className="w-4 h-4 text-blue-700" />
                  <div><strong>Preview mode</strong> — rows parsed, no AI recommendation run yet. Click <em>Continue with Recommend</em> to classify.</div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={reset} className="h-8 text-xs" data-testid="btn-cancel-preview">Cancel</Button>
                  <Button onClick={() => doUpload(false)} disabled={busy} className="bg-blue-700 hover:bg-blue-800 h-8 text-xs" data-testid="btn-continue-recommend">
                    {busy ? <><Sparkles className="w-3.5 h-3.5 mr-1 animate-pulse" />Recommending…</> :
                            <><Brain className="w-3.5 h-3.5 mr-1" />Continue with Recommend</>}
                  </Button>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="preview-summary">
              <StatTile label="Rows parsed"      value={preview.summary.total} />
              <StatTile label="Auto-recommended" value={preview.summary.auto_recommended} accent="emerald" />
              <StatTile label="Inflow ₹"  value={formatINR(preview.summary.total_inflow)}  accent="emerald" />
              <StatTile label="Outflow ₹" value={formatINR(preview.summary.total_outflow)} accent="rose" />
              <StatTile label="Duplicates" value={preview.summary.duplicates} accent={preview.summary.duplicates ? "amber" : "gray"} />
            </div>

            {preview.reconciliation && preview.reconciliation.statement_closing != null && (
              <ReconciliationCard
                rec={preview.reconciliation}
                ledgerName={preview.ledger_name}
                onReconcile={reconcileOpening}
                reconciling={reconciling}
              />
            )}

            {/* Master toggles bar */}
            <div className="flex items-center justify-between border border-gray-200 rounded bg-white p-2.5 flex-wrap gap-2" data-testid="master-toggles">
              <div className="flex items-center gap-5 flex-wrap">
                <label className="flex items-center gap-2 text-xs cursor-pointer font-medium">
                  <input
                    type="checkbox"
                    checked={autoApproveHighConf}
                    onChange={(e) => {
                      const v = e.target.checked;
                      setAutoApproveHighConf(v);
                      // Retroactively toggle approval on high-confidence rows
                      setOverrides((o) => {
                        const next = { ...o };
                        (preview.rows || []).forEach((r, i) => {
                          const hi = (r.confidence || 0) >= HIGH_CONFIDENCE_THRESHOLD;
                          if (hi && next[i]?.contra_ledger_id) next[i] = { ...next[i], approved: v };
                        });
                        return next;
                      });
                    }}
                    data-testid="toggle-auto-approve"
                  />
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  Auto-Approve High Confidence Entries (≥ {HIGH_CONFIDENCE_THRESHOLD}%)
                </label>
                <label className="flex items-center gap-2 text-xs cursor-pointer font-medium">
                  <input
                    type="checkbox"
                    checked={groupIdentical}
                    onChange={(e) => setGroupIdentical(e.target.checked)}
                    data-testid="toggle-group-narrations"
                  />
                  <Layers className="w-4 h-4 text-blue-600" />
                  Group Identical Narrations
                </label>
              </div>
              <div className="text-[11px] text-gray-500 hidden md:block">
                <kbd className="px-1.5 py-0.5 bg-gray-100 border border-gray-300 rounded font-mono">↑↓</kbd> to move ·
                <kbd className="px-1.5 py-0.5 bg-gray-100 border border-gray-300 rounded font-mono ml-1">Enter</kbd> to open dropdown ·
                <kbd className="px-1.5 py-0.5 bg-gray-100 border border-gray-300 rounded font-mono ml-1">Space</kbd> to toggle
              </div>
            </div>

            {/* Filter / search / bulk actions toolbar */}
            <div className="flex flex-wrap items-center gap-2 border border-gray-200 rounded bg-white p-2.5">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-2 top-2.5 w-3.5 h-3.5 text-gray-400" />
                <Input
                  placeholder="Search narration, ref, ledger…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="h-8 pl-7 text-sm"
                  data-testid="input-search-rows"
                />
              </div>
              <select value={filterSource} onChange={(e) => setFilterSource(e.target.value)} className="h-8 rounded border border-gray-300 px-2 text-xs" data-testid="select-source-filter">
                <option value="all">All sources</option>
                <option value="learning">Learned only</option>
                <option value="rule">Rule only</option>
                <option value="ai">AI only</option>
                <option value="none">Unmapped only</option>
              </select>
              <select onChange={(e) => { if (e.target.value) { bulkAssign(e.target.value); e.target.value = ""; } }} className="h-8 rounded border border-gray-300 px-2 text-xs" data-testid="select-bulk-assign">
                <option value="">Bulk assign ledger…</option>
                {contraLedgers.map((L) => <option key={L.id} value={L.id}>{L.name}</option>)}
              </select>
              <Button variant="outline" onClick={() => selectAll(true)} className="h-8 text-xs" data-testid="btn-select-all">
                <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Select visible
              </Button>
              <Button variant="outline" onClick={() => selectAll(false)} className="h-8 text-xs" data-testid="btn-deselect-all">
                Clear
              </Button>
              <label className="flex items-center gap-1.5 text-xs ml-auto">
                <input type="checkbox" checked={skipDupes} onChange={(e) => setSkipDupes(e.target.checked)} data-testid="checkbox-skip-dupes" />
                <Lock className="w-3 h-3 text-amber-600" /> Skip duplicates ({preview.summary.duplicates})
              </label>
              <Button variant="ghost" onClick={reset} className="h-8 text-xs" data-testid="btn-reset">
                <RotateCcw className="w-3.5 h-3.5 mr-1" /> Discard
              </Button>
              <Button
                onClick={postSelected}
                disabled={posting || approvedCount === 0 || invalidSplits.length > 0}
                className="bg-emerald-700 hover:bg-emerald-800 h-8 text-xs"
                data-testid="btn-post-vouchers"
              >
                <Wand2 className="w-3.5 h-3.5 mr-1" />
                {posting ? "Posting…" : `Import ${approvedCount} entr${approvedCount === 1 ? "y" : "ies"}`}
              </Button>
            </div>

            {invalidSplits.length > 0 && (
              <div className="rounded border border-rose-200 bg-rose-50 p-2 text-xs text-rose-800 flex items-center gap-2" data-testid="split-validation-error">
                <AlertTriangle className="w-4 h-4" />
                <span>{invalidSplits.length} split{invalidSplits.length === 1 ? " has" : "s have"} a mismatched total. Fix them before importing.</span>
              </div>
            )}

            {/* Grouped view */}
            {groupIdentical && grouped && (
              <div className="border border-gray-200 rounded bg-white overflow-hidden" data-testid="grouped-view">
                <div className="overflow-x-auto max-h-[70vh] overflow-y-auto">
                  <table className="tx-table text-xs">
                    <thead className="sticky top-0 bg-white z-10 shadow-[0_1px_0_rgba(0,0,0,0.06)]">
                      <tr>
                        <th className="w-6"></th>
                        <th>Narration group</th>
                        <th className="text-right w-24">Count</th>
                        <th className="text-right w-24">Total Debit</th>
                        <th className="text-right w-24">Total Credit</th>
                        <th className="w-56">Assign ledger to all</th>
                      </tr>
                    </thead>
                    <tbody>
                      {grouped.map((g) => {
                        const key = g.key || "(unknown)";
                        const isOpen = Boolean(expandedGroups[key]);
                        const totDr = g.items.reduce((s, r) => s + (r.withdrawal || 0), 0);
                        const totCr = g.items.reduce((s, r) => s + (r.deposit || 0), 0);
                        return (
                          <Fragment key={`grp-frag-${key}`}>
                            <tr className="bg-gray-50 font-medium" data-testid={`group-row-${key}`}>
                              <td>
                                <button onClick={() => setExpandedGroups((s) => ({ ...s, [key]: !isOpen }))} className="text-gray-600" data-testid={`btn-toggle-group-${key}`}>
                                  {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                                </button>
                              </td>
                              <td className="truncate max-w-[420px]" title={g.sample.description}>{g.sample.description}</td>
                              <td className="tx-num">{g.items.length}</td>
                              <td className="tx-num text-rose-700">{totDr > 0 ? formatINR(totDr) : ""}</td>
                              <td className="tx-num text-emerald-700">{totCr > 0 ? formatINR(totCr) : ""}</td>
                              <td>
                                <select
                                  onChange={(e) => applyLedgerToGroup(g.items.map((r) => r._idx), e.target.value)}
                                  defaultValue=""
                                  className="h-7 w-full rounded border border-gray-300 px-1 text-[11px]"
                                  data-testid={`select-group-ledger-${key}`}
                                >
                                  <option value="">-- pick ledger for all --</option>
                                  {contraLedgers.map((L) => (<option key={L.id} value={L.id}>{L.name}</option>))}
                                </select>
                              </td>
                            </tr>
                            {isOpen && g.items.map((r) => (
                              <TxnRow
                                key={`row-${r._idx}`}
                                row={r} idx={r._idx}
                                ledgers={contraLedgers}
                                ov={overrides[r._idx] || {}}
                                splits={splitsByRow[r._idx]}
                                expandedSplits={expandedSplits[r._idx]}
                                focused={focusedRowIdx === r._idx}
                                onToggle={() => toggleRow(r._idx)}
                                onSetLedger={(lid) => setRowLedger(r._idx, lid)}
                                onSetNarration={(v) => setRowNarration(r._idx, v)}
                                onReclassify={() => reclassifyRow(r._idx)}
                                onOpenSplit={() => openSplit(r._idx)}
                                onCloseSplit={() => closeSplit(r._idx)}
                                onUpdateSplitLine={(k, patch) => updateSplitLine(r._idx, k, patch)}
                                onAddSplitLine={() => addSplitLine(r._idx)}
                                onRemoveSplitLine={(k) => removeSplitLine(r._idx, k)}
                                onBalanceSplit={() => balanceSplit(r._idx)}
                                onOpenRule={() => openRuleModal(r._idx)}
                                onRequestCreateLedger={() => requestCreateLedger(r._idx)}
                                onRequestCreateLedgerSplit={(k) => requestCreateLedger(r._idx, k)}
                                setRowRef={(el) => { rowRefs.current[r._idx] = el; }}
                                setLedgerRef={(el) => { ledgerSelectRefs.current[r._idx] = el; }}
                                onFocus={() => setFocusedRowIdx(r._idx)}
                              />
                            ))}
                          </Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Flat review table */}
            {!groupIdentical && (
              <div className="border border-gray-200 rounded bg-white overflow-hidden">
                <div className="overflow-x-auto max-h-[70vh] overflow-y-auto">
                  <table className="tx-table text-xs">
                    <thead className="sticky top-0 bg-white shadow-[0_1px_0_rgba(0,0,0,0.06)] z-10">
                      <tr>
                        <th className="w-8"></th>
                        <th className="w-24">Date</th>
                        <th>Narration</th>
                        <th className="text-right w-24">Withdrawal (Dr)</th>
                        <th className="text-right w-24">Deposit (Cr)</th>
                        <th className="w-56">Destination Ledger</th>
                        <th className="w-36">Confidence</th>
                        <th className="w-32 text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.length === 0 && (
                        <tr><td colSpan={8} className="text-center py-10 text-gray-400">No rows match the current filter.</td></tr>
                      )}
                      {filtered.map((r) => (
                        <TxnRow
                          key={`row-${r._idx}`}
                          row={r} idx={r._idx}
                          ledgers={contraLedgers}
                          ov={overrides[r._idx] || {}}
                          splits={splitsByRow[r._idx]}
                          expandedSplits={expandedSplits[r._idx]}
                          focused={focusedRowIdx === r._idx}
                          onToggle={() => toggleRow(r._idx)}
                          onSetLedger={(lid) => setRowLedger(r._idx, lid)}
                          onSetNarration={(v) => setRowNarration(r._idx, v)}
                          onReclassify={() => reclassifyRow(r._idx)}
                          onOpenSplit={() => openSplit(r._idx)}
                          onCloseSplit={() => closeSplit(r._idx)}
                          onUpdateSplitLine={(k, patch) => updateSplitLine(r._idx, k, patch)}
                          onAddSplitLine={() => addSplitLine(r._idx)}
                          onRemoveSplitLine={(k) => removeSplitLine(r._idx, k)}
                          onBalanceSplit={() => balanceSplit(r._idx)}
                          onOpenRule={() => openRuleModal(r._idx)}
                          onRequestCreateLedger={() => requestCreateLedger(r._idx)}
                          onRequestCreateLedgerSplit={(k) => requestCreateLedger(r._idx, k)}
                          setRowRef={(el) => { rowRefs.current[r._idx] = el; }}
                          setLedgerRef={(el) => { ledgerSelectRefs.current[r._idx] = el; }}
                          onFocus={() => setFocusedRowIdx(r._idx)}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}

        {/* Create Rule modal */}
        {ruleModal && (
          <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40" data-testid="rule-modal">
            <div className="bg-white rounded shadow-lg w-full max-w-md border border-gray-200">
              <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                <h3 className="font-semibold text-sm flex items-center gap-2">
                  <FilePlus2 className="w-4 h-4 text-blue-700" /> Create a mapping rule
                </h3>
                <button onClick={() => setRuleModal(null)} className="text-gray-500 hover:text-gray-800" data-testid="btn-close-rule-modal">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="p-4 space-y-3 text-sm">
                <p className="text-xs text-gray-600 leading-relaxed">
                  Any future transaction whose narration contains the keyword below will
                  auto-map to the chosen ledger. The rule is applied instantly to matching rows in this statement too.
                </p>
                <div className="space-y-1.5">
                  <label className="text-[11px] uppercase text-gray-600">Keyword</label>
                  <Input
                    value={ruleModal.keyword}
                    onChange={(e) => setRuleModal({ ...ruleModal, keyword: e.target.value })}
                    placeholder="e.g. uber, swiggy, salary"
                    data-testid="input-rule-keyword"
                    className="h-9"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] uppercase text-gray-600">Ledger</label>
                  <select
                    value={ruleModal.ledger_id}
                    onChange={(e) => setRuleModal({ ...ruleModal, ledger_id: e.target.value })}
                    className="h-9 w-full rounded border border-gray-300 px-2 text-sm bg-white"
                    data-testid="select-rule-ledger"
                  >
                    <option value="">-- pick a ledger --</option>
                    {contraLedgers.map((L) => <option key={L.id} value={L.id}>{L.name} · {L.group}</option>)}
                  </select>
                </div>
                <div className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded p-2">
                  <em>Always map transactions containing &ldquo;<strong>{ruleModal.keyword || "…"}</strong>&rdquo; to <strong>{ledgers.find((L) => L.id === ruleModal.ledger_id)?.name || "(select a ledger)"}</strong> in the future.</em>
                </div>
              </div>
              <div className="px-4 py-3 border-t border-gray-100 flex justify-end gap-2">
                <Button variant="outline" onClick={() => setRuleModal(null)} className="h-8" data-testid="btn-cancel-rule">Cancel</Button>
                <Button onClick={saveRule} disabled={!ruleModal.keyword.trim() || !ruleModal.ledger_id} className="h-8 bg-blue-700 hover:bg-blue-800" data-testid="btn-save-rule">
                  Save Rule
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Quick-create ledger modal (opened from the destination dropdown) */}
        {createLedger && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="create-ledger-modal">
            <div className="bg-white rounded shadow-lg w-full max-w-md border border-gray-200">
              <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                <h3 className="font-semibold text-sm flex items-center gap-2">
                  <FilePlus2 className="w-4 h-4 text-blue-700" /> Create a new ledger
                </h3>
                <button onClick={() => setCreateLedger(null)} className="text-gray-500 hover:text-gray-800" data-testid="btn-close-new-ledger-modal">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="p-4 space-y-3 text-sm">
                <p className="text-xs text-gray-600 leading-relaxed">
                  The ledger is created instantly and auto-selected for this transaction.
                </p>
                <div className="space-y-1.5">
                  <label className="text-[11px] uppercase text-gray-600">Ledger name</label>
                  <Input
                    autoFocus
                    value={createLedger.name}
                    onChange={(e) => setCreateLedger({ ...createLedger, name: e.target.value })}
                    onKeyDown={(e) => { if (e.key === "Enter" && createLedger.name.trim() && createLedger.group) saveNewLedger(); }}
                    placeholder="e.g. Office Rent, Electricity Charges"
                    className="h-9"
                    data-testid="input-new-ledger-name"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] uppercase text-gray-600">Group</label>
                  <select
                    value={createLedger.group}
                    onChange={(e) => setCreateLedger({ ...createLedger, group: e.target.value })}
                    className="h-9 w-full rounded border border-gray-300 px-2 text-sm bg-white"
                    data-testid="select-new-ledger-group"
                  >
                    <option value="">-- pick a group --</option>
                    {groupOptions.map((g) => <option key={g} value={g}>{g}</option>)}
                  </select>
                </div>
              </div>
              <div className="px-4 py-3 border-t border-gray-100 flex justify-end gap-2">
                <Button variant="outline" onClick={() => setCreateLedger(null)} className="h-8" data-testid="btn-cancel-new-ledger">Cancel</Button>
                <Button onClick={saveNewLedger} disabled={!createLedger.name.trim() || !createLedger.group} className="h-8 bg-blue-700 hover:bg-blue-800" data-testid="btn-save-new-ledger">
                  Create &amp; Assign
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

/* ───────────────────────────────────────────────────────────────────────
   Single transaction row — extracted so it renders identically in both
   flat and grouped views without duplicating the JSX / props.
   ─────────────────────────────────────────────────────────────────────── */
function TxnRow({
  row: r, idx, ledgers, ov, splits, expandedSplits, focused,
  onToggle, onSetLedger, onSetNarration, onReclassify, onOpenSplit, onCloseSplit,
  onUpdateSplitLine, onAddSplitLine, onRemoveSplitLine, onBalanceSplit, onOpenRule,
  onRequestCreateLedger, onRequestCreateLedgerSplit,
  setRowRef, setLedgerRef, onFocus,
}) {
  const badge = SOURCE_BADGE[r.source || ""];
  const confidence = r.confidence || 0;
  const isHigh = confidence >= HIGH_CONFIDENCE_THRESHOLD && r.source;
  const isReview = !isHigh && r.source && confidence < HIGH_CONFIDENCE_THRESHOLD && confidence >= 40;
  const hasSplits = (splits || []).length > 0;
  const originalTotal = r.deposit > 0 ? r.deposit : r.withdrawal;
  const splitSum = (splits || []).reduce((s, x) => s + Number(x.amount || 0), 0);
  const splitValid = hasSplits ? Math.abs(splitSum - originalTotal) < 0.01 : true;

  return (
    <>
      <tr
        ref={setRowRef}
        onClick={onFocus}
        className={`${r.duplicate_likely ? "bg-amber-50/40" : ""} ${ov.approved ? "ring-1 ring-emerald-200/60" : ""} ${focused ? "bg-blue-50/60" : ""}`}
        data-testid={`row-stmt-${idx}`}
      >
        <td>
          <input
            type="checkbox"
            checked={Boolean(ov.approved)}
            onChange={onToggle}
            disabled={!ov.contra_ledger_id && !hasSplits}
            data-testid={`checkbox-approve-${idx}`}
          />
        </td>
        <td className="font-mono text-[11px]">{r.date}</td>
        <td>
          <div className="flex items-start gap-1">
            <NarrationField value={ov.narration_override ?? r.description} isOverride={Boolean(ov.narration_override && ov.narration_override !== r.description)} onCommit={onSetNarration} idx={idx} />
            {r.duplicate_likely && (
              <span title="A voucher with the same date+amount+narration already exists">
                <AlertTriangle className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" />
              </span>
            )}
          </div>
          {r.reason && <div className="text-[10px] text-gray-500 mt-0.5 italic">↳ {r.reason}</div>}
        </td>
        <td className="tx-num text-rose-700">{r.withdrawal > 0 && (<><ArrowUpRight className="inline w-3 h-3" /> {formatINR(r.withdrawal)}</>)}</td>
        <td className="tx-num text-emerald-700">{r.deposit > 0 && (<><ArrowDownLeft className="inline w-3 h-3" /> {formatINR(r.deposit)}</>)}</td>
        <td>
          <div className="flex items-center gap-1">
            <select
              ref={setLedgerRef}
              value={hasSplits ? "" : (ov.contra_ledger_id || "")}
              onChange={(e) => { if (e.target.value === NEW_LEDGER) { onRequestCreateLedger(); } else { onSetLedger(e.target.value); } }}
              disabled={hasSplits}
              className={`h-7 flex-1 min-w-0 rounded border px-1.5 text-[11px] ${hasSplits ? "bg-gray-100 border-gray-200 text-gray-400" : "border-gray-300"}`}
              data-testid={`select-ledger-${idx}`}
            >
              <option value="">{hasSplits ? "-- SPLIT --" : "-- pick a ledger --"}</option>
              {!hasSplits && <option value={NEW_LEDGER} className="font-semibold text-blue-700">➕ Create new ledger…</option>}
              {ledgers.map((L) => (<option key={L.id} value={L.id}>{L.name} · {L.group}</option>))}
            </select>
            <button
              onClick={onOpenRule}
              title="Create a mapping rule from this row"
              className="text-blue-700 hover:text-blue-900 shrink-0"
              data-testid={`btn-create-rule-${idx}`}
            >
              <FilePlus2 className="w-3.5 h-3.5" />
            </button>
            {(!r.suggestion || r.source === "") && (
              <button
                onClick={onReclassify}
                title="Re-run AI on this row"
                className="text-purple-700 hover:text-purple-900 shrink-0"
                data-testid={`btn-reclassify-${idx}`}
              >
                <Zap className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </td>
        <td>
          <div className="space-y-1">
            <ConfidenceBar value={confidence} />
            <div className="flex items-center gap-1">
              {isHigh && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-100 text-emerald-800 border border-emerald-200" data-testid={`badge-high-conf-${idx}`}>
                  <CheckCircle2 className="w-3 h-3" /> High Confidence
                </span>
              )}
              {isReview && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-800 border border-amber-200" data-testid={`badge-review-${idx}`}>
                  <AlertTriangle className="w-3 h-3" /> Review Needed
                </span>
              )}
              {ov.approved && isHigh && (
                <span className="text-[10px] text-emerald-700 font-medium" data-testid={`badge-ready-${idx}`}>Ready</span>
              )}
            </div>
            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] ${badge.cls}`}>
              {badge.label}
            </span>
          </div>
        </td>
        <td className="text-center">
          {!expandedSplits ? (
            <button
              onClick={onOpenSplit}
              className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded border border-blue-200 text-blue-700 hover:bg-blue-50"
              title="Split this transaction across multiple ledgers"
              data-testid={`btn-split-${idx}`}
            >
              <Split className="w-3 h-3" /> Split
            </button>
          ) : (
            <button
              onClick={onCloseSplit}
              className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded border border-rose-200 text-rose-700 hover:bg-rose-50"
              title="Cancel splitting — restore single ledger mode"
              data-testid={`btn-cancel-split-${idx}`}
            >
              <X className="w-3 h-3" /> Cancel
            </button>
          )}
        </td>
      </tr>

      {expandedSplits && (
        <tr data-testid={`split-panel-${idx}`}>
          <td colSpan={8} className="bg-blue-50/30 border-l-2 border-blue-500 p-3">
            <div className="text-xs font-semibold text-blue-900 mb-2 flex items-center gap-2">
              <Split className="w-3.5 h-3.5" /> Splitting ₹{formatINR(originalTotal)} across {splits?.length || 0} ledger(s)
              <span className={`ml-auto px-2 py-0.5 rounded text-[10px] font-medium ${splitValid ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"}`} data-testid={`split-status-${idx}`}>
                {splitValid ? `Balanced: ₹${formatINR(splitSum)}` : `Off by ₹${formatINR(originalTotal - splitSum)}`}
              </span>
            </div>
            <table className="w-full text-xs">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-gray-500">
                  <th className="text-left w-8">#</th>
                  <th className="text-left">Ledger</th>
                  <th className="text-right w-32">Amount ₹</th>
                  <th>Narration</th>
                  <th className="w-8"></th>
                </tr>
              </thead>
              <tbody>
                {(splits || []).map((s, k) => (
                  <tr key={k}>
                    <td className="text-gray-500 font-mono">{k + 1}</td>
                    <td>
                      <select
                        value={s.contra_ledger_id}
                        onChange={(e) => { if (e.target.value === NEW_LEDGER) { onRequestCreateLedgerSplit(k); } else { onUpdateSplitLine(k, { contra_ledger_id: e.target.value }); } }}
                        className="h-7 w-full rounded border border-gray-300 px-1 text-[11px] bg-white"
                        data-testid={`select-split-ledger-${idx}-${k}`}
                      >
                        <option value="">-- pick ledger --</option>
                        <option value={NEW_LEDGER} className="font-semibold text-blue-700">➕ Create new ledger…</option>
                        {ledgers.map((L) => <option key={L.id} value={L.id}>{L.name}</option>)}
                      </select>
                    </td>
                    <td>
                      <Input
                        type="number" step="0.01"
                        value={s.amount}
                        onChange={(e) => onUpdateSplitLine(k, { amount: Number(e.target.value) })}
                        className="h-7 text-right font-mono text-[11px]"
                        data-testid={`input-split-amount-${idx}-${k}`}
                      />
                    </td>
                    <td>
                      <Input
                        value={s.narration || ""}
                        onChange={(e) => onUpdateSplitLine(k, { narration: e.target.value })}
                        placeholder="(optional)"
                        className="h-7 text-[11px]"
                      />
                    </td>
                    <td>
                      <button
                        onClick={() => onRemoveSplitLine(k)}
                        disabled={(splits || []).length <= 2}
                        className="text-rose-700 disabled:text-gray-300"
                        title={(splits || []).length <= 2 ? "Minimum 2 split lines" : "Remove this line"}
                        data-testid={`btn-remove-split-line-${idx}-${k}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex items-center gap-2 mt-2">
              <Button variant="outline" size="sm" onClick={onAddSplitLine} className="h-7 text-[11px]" data-testid={`btn-add-split-line-${idx}`}>
                <FilePlus2 className="w-3 h-3 mr-1" /> Add line
              </Button>
              <Button variant="outline" size="sm" onClick={onBalanceSplit} className="h-7 text-[11px]" data-testid={`btn-balance-split-${idx}`}>
                <CheckCircle2 className="w-3 h-3 mr-1" /> Auto-balance last line
              </Button>
              <span className="text-[10px] text-gray-500 ml-auto">Total must equal ₹{formatINR(originalTotal)} exactly.</span>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

/* Inline editable narration cell (unchanged from prior). */
const NarrationField = ({ value, isOverride, onCommit, idx }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  useEffect(() => { if (!editing) setDraft(value); }, [value, editing]);
  if (!editing) {
    return (
      <div className="flex items-center gap-1 group flex-1 min-w-0">
        <span className={`line-clamp-2 break-words ${isOverride ? "text-purple-800 italic" : ""}`} title={value}>{value}</span>
        <button onClick={() => setEditing(true)} className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-gray-700 shrink-0" title="Edit narration before posting" data-testid={`btn-edit-narration-${idx}`}>
          <Edit3 className="w-3 h-3" />
        </button>
      </div>
    );
  }
  return (
    <textarea
      autoFocus
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => { onCommit(draft); setEditing(false); }}
      onKeyDown={(e) => {
        if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); onCommit(draft); setEditing(false); }
        if (e.key === "Escape") { setEditing(false); }
      }}
      rows={2}
      className="flex-1 min-w-0 text-[11px] rounded border border-purple-300 p-1 focus:outline-none focus:ring-1 focus:ring-purple-500"
      data-testid={`textarea-narration-${idx}`}
    />
  );
};

const StatTile = ({ label, value, accent = "gray" }) => {
  const accents = {
    emerald: "border-emerald-200 bg-emerald-50",
    rose:    "border-rose-200    bg-rose-50",
    amber:   "border-amber-200   bg-amber-50",
    gray:    "border-gray-200    bg-white",
  };
  return (
    <div className={`rounded border ${accents[accent] || accents.gray} px-3 py-2`}>
      <div className="text-[10px] uppercase tracking-wider text-gray-500">{label}</div>
      <div className="text-base font-semibold mt-0.5">{value}</div>
    </div>
  );
};

/** Closing-balance reconciliation panel. Makes it explicit whether this
 *  statement will tally after posting — and offers a one-click fix when the
 *  bank ledger's opening balance doesn't match the statement's opening. */
const ReconciliationCard = ({ rec, ledgerName, onReconcile, reconciling }) => {
  const parseBroken = rec.parse_tallies === false;
  const openingOff = !parseBroken && rec.ledger_opening_matches === false;
  const willTally = !parseBroken && rec.projected_matches_statement === true;

  const tone = parseBroken ? "rose" : openingOff ? "amber" : "emerald";
  const toneCls = {
    rose:    "border-rose-200 bg-rose-50",
    amber:   "border-amber-200 bg-amber-50",
    emerald: "border-emerald-200 bg-emerald-50",
  }[tone];
  const Icon = willTally ? CheckCircle2 : AlertTriangle;
  const iconCls = { rose: "text-rose-600", amber: "text-amber-600", emerald: "text-emerald-600" }[tone];

  return (
    <div className={`rounded border ${toneCls} p-3.5`} data-testid="reconciliation-card">
      <div className="flex items-start gap-2.5">
        <Icon className={`w-5 h-5 mt-0.5 shrink-0 ${iconCls}`} />
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm flex items-center gap-2" data-testid="reconciliation-status">
            {willTally && "Closing balance will tally ✓"}
            {openingOff && "Closing balance won't tally — opening balance mismatch"}
            {parseBroken && "Closing balance won't tally — some transactions couldn't be read"}
          </div>

          {/* Statement equation */}
          <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs font-mono">
            <span className="text-gray-500">Opening</span>
            <span className="font-semibold">{formatINR(rec.statement_opening)}</span>
            <span className="text-emerald-700">+ Credits {formatINR(rec.total_deposits)}</span>
            <span className="text-rose-700">− Debits {formatINR(rec.total_withdrawals)}</span>
            <span className="text-gray-500">=</span>
            <span className="font-semibold">Closing {formatINR(rec.statement_closing)}</span>
          </div>

          {parseBroken && (
            <p className="mt-2 text-xs text-rose-800 leading-relaxed">
              The parsed rows add up to <strong>{formatINR(rec.computed_closing)}</strong>, but the statement's printed
              closing balance is <strong>{formatINR(rec.statement_closing)}</strong> (off by {formatINR(Math.abs(rec.parse_diff || 0))}).
              One or more transactions were likely missed or misread — please review the rows before posting, or re-upload a clearer copy.
            </p>
          )}

          {openingOff && (
            <div className="mt-2 text-xs text-amber-900 leading-relaxed">
              <p>
                Your <strong>{ledgerName}</strong> ledger currently carries <strong>{formatINR(rec.ledger_opening_before_period)}</strong> going
                into this period, but the statement opens at <strong>{formatINR(rec.statement_opening)}</strong>. After
                posting, your ledger would close at <strong>{formatINR(rec.projected_ledger_closing)}</strong> instead of
                the statement's <strong>{formatINR(rec.statement_closing)}</strong> — a gap of {formatINR(Math.abs(rec.opening_gap || 0))}.
              </p>
              {rec.existing_in_period_count > 0 && (
                <p className="mt-1">
                  Note: {rec.existing_in_period_count} existing voucher(s) already touch this bank inside the statement period — check for duplicates.
                </p>
              )}
              <Button
                onClick={onReconcile}
                disabled={reconciling}
                className="mt-2 h-8 text-xs bg-amber-600 hover:bg-amber-700"
                data-testid="btn-reconcile-opening"
              >
                {reconciling ? "Setting…" : `Set opening balance to ${formatINR(rec.statement_opening)} so it tallies`}
              </Button>
            </div>
          )}

          {willTally && (
            <p className="mt-2 text-xs text-emerald-800 leading-relaxed" data-testid="reconciliation-tally-ok">
              After posting the approved rows, <strong>{ledgerName}</strong> will close at{" "}
              <strong>{formatINR(rec.statement_closing)}</strong>, exactly matching the statement.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default function BankImportRecommend() {
  return <RequireCompany><Inner /></RequireCompany>;
}
